import { useState } from "react";
import { api, fmtNum } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronRight, CheckCircle2, Gavel } from "lucide-react";

const TIPO = {
  sin_decision: "Deuda sin decisión",
  revision_vencida: "Revisión vencida",
  sin_gestion: "Sin gestión",
  promesa_vencida: "Promesa vencida",
  pagare: "Pagaré sin firmar",
};
const NIVEL = {
  alto: "bg-red-50 text-red-700 border-red-200",
  medio: "bg-amber-50 text-amber-700 border-amber-200",
};
const PLAZOS = [
  ["dias_deuda_gestion", "Deuda vencida exige gestión desde (días de atraso)"],
  ["dias_sin_gestion", "Se reclama si no hay gestión en (días)"],
  ["dias_deuda_decision", "Deuda exige decisión desde (días de atraso)"],
  ["dias_pagare", "Pagaré sin firmar desde (días de emisión)"],
  ["dias_juicio_sin_mov", "Caso judicial sin seguimiento desde (días)"],
];

function Chip({ nivel, children }) {
  return <span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 ${NIVEL[nivel]}`}>{children}</span>;
}

function Grupo({ g, abierto, onCliente }) {
  const [open, setOpen] = useState(abierto);
  return (
    <div className="border border-slate-200 rounded-xl bg-white">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-4 py-3 text-left">
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        <span className="font-semibold text-emerald-950 flex-1">{g.vendedor}</span>
        {g.altos > 0 && <Chip nivel="alto">{g.altos} urgente{g.altos > 1 ? "s" : ""}</Chip>}
        {g.medios > 0 && <Chip nivel="medio">{g.medios} a atender</Chip>}
        <span className="text-xs text-slate-500 hidden sm:inline">USD {fmtNum(g.monto)} en juego</span>
      </button>
      {open && (
        <ul className="divide-y divide-slate-100 border-t border-slate-100">
          {g.items.map((r) => (
            <li key={r.id} className="px-4 py-3 text-sm space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Chip nivel={r.nivel}>{r.nivel === "alto" ? "URGENTE" : "ATENDER"}</Chip>
                <span className="text-[11px] font-semibold text-slate-500">{TIPO[r.tipo] || r.tipo}</span>
                {onCliente && <button onClick={() => onCliente(r.cliente)} className="ml-auto text-[11px] text-emerald-700 underline">Ver cliente</button>}
              </div>
              <p className="text-slate-800">{r.mensaje}</p>
              <p className="text-[12px] text-emerald-800"><b>Qué hacer:</b> {r.accion}</p>
            </li>))}
        </ul>)}
    </div>
  );
}

export default function GestionReclamos({ data, onReload, onCliente }) {
  const [cfg, setCfg] = useState(null);
  if (!data) return <p className="text-sm text-slate-400">Cargando pendientes…</p>;
  const grupos = data.por_vendedor || [];
  const jud = data.judiciales;
  const guardar = async () => {
    try {
      await api.put("/gestion/config", Object.fromEntries(Object.entries(cfg).map(([k, v]) => [k, Number(v)])));
      toast.success("Plazos guardados");
      setCfg(null); onReload && onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudieron guardar los plazos"); }
  };
  return (
    <div className="space-y-5 max-w-5xl" data-testid="gestion-reclamos">
      <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="font-heading text-lg font-bold text-emerald-950">Pendientes de gestión</h3>
          {data.altos > 0 && <Chip nivel="alto">{data.altos} urgente{data.altos > 1 ? "s" : ""}</Chip>}
          {data.medios > 0 && <Chip nivel="medio">{data.medios} a atender</Chip>}
        </div>
        <p className="text-xs text-slate-500">
          La página revisa la cartera cada vez que se abre y le reclama a cada responsable lo que le corresponde cuidar: pagarés de más de {data.config.dias_pagare} días sin firmar,
          deudas atrasadas sin decisión, deudas sin gestión en {data.config.dias_sin_gestion} días y promesas de pago vencidas. Cada reclamo desaparece cuando se atiende:
          registrando la gestión en el cliente, definiendo la deuda en "Deudas +60 días" o firmando el pagaré. Los clientes que ya están en juicio se siguen en Casos judiciales.
        </p>
        {data.puede_config && !cfg && <button onClick={() => setCfg({ ...data.config })} className="text-[11px] text-emerald-700 underline">Cambiar los plazos</button>}
        {cfg && (
          <div className="rounded-lg border border-slate-200 p-3 space-y-2">
            <div className="grid sm:grid-cols-2 gap-2">
              {PLAZOS.map(([k, t]) => (
                <label key={k} className="text-xs text-slate-500">{t}
                  <input type="number" min="1" value={cfg[k]} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} className="block mt-1 w-24 border border-slate-200 rounded-md px-2 py-1 text-sm" /></label>))}
            </div>
            <div className="flex gap-3 text-xs"><button onClick={guardar} className="bg-emerald-700 text-white px-3 py-1.5 rounded-md">Guardar plazos</button>
              <button onClick={() => setCfg(null)} className="text-slate-400">Cancelar</button></div>
          </div>)}
      </div>

      {grupos.length === 0 ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex items-center gap-3 text-emerald-900 text-sm">
          <CheckCircle2 className="h-5 w-5" /> No hay pendientes de gestión. Todo lo que le corresponde está al día.
        </div>
      ) : (
        <div className="space-y-3">{grupos.map((g) => <Grupo key={g.vendedor} g={g} abierto={grupos.length === 1} onCliente={onCliente} />)}</div>)}

      {jud && jud.length > 0 && (
        <div className="rounded-2xl border border-violet-200 bg-white p-5 shadow-sm space-y-3">
          <div className="flex items-center gap-2 font-heading font-bold text-violet-900"><Gavel className="h-4 w-4" /> Casos judiciales con acciones urgentes ({jud.length})</div>
          <ul className="divide-y divide-slate-100">
            {jud.slice(0, 30).map((c) => (
              <li key={c.id} className="py-2 text-sm">
                <div className="font-medium">{c.cliente} <span className="text-[11px] text-slate-400 font-normal">{c.abogado ? `· ${c.abogado}` : "· sin abogado"} · USD {fmtNum(c.valor_real)}</span></div>
                {c.acciones.filter((a) => a.nivel === "alto").map((a, i) => (
                  <div key={i} className="text-[12px] text-slate-600"><AlertTriangle className="inline h-3 w-3 text-red-600 mr-1" /><b>{a.accion}</b> — {a.motivo}</div>))}
              </li>))}
          </ul>
        </div>)}
    </div>
  );
}
